//Thermostat Readings
//Site Slicer - vwSite - SiteName
//Date Slicer - vwReportingDateTime - Date
//Thermostat Slicer - vwControl - FriendlyName

import constants from '../constants';

let firstSiteName = '';
let firstLoad = true;

/**
 * Sets the slicer visuals for the report.
 * @param {Object} reportParameters - The report parameters.
 * @param {Array} visuals - The visuals array.
 * @returns {Promise<void>}
 */
const setSlicers = async (reportParameters, visuals) => {
  await setSiteSlicerVisual(reportParameters, visuals);
  await setDateSlicerVisual(reportParameters, visuals);
  await setThermostatSlicerVisual(reportParameters, visuals);
};

/**
 * Sets the site slicer visual based on the SiteName parameter.
 * @param {Object} reportParameters - The report parameters.
 * @param {Array} visuals - The visuals array.
 * @returns {Promise<void>}
 */
const setSiteSlicerVisual = async (reportParameters, visuals) => {
  try {
    const siteData = getSiteDataBySiteName(reportParameters);
    const { SiteName } = reportParameters;

    const siteSlicerVisual = visuals.filter(function (visual) {
      return visual.type === 'slicer' && visual.title === 'Site Slicer';
    })[0];

    const values = siteData ? [siteData.SiteName] : [SiteName];

    if (!SiteName && !siteData) {
      const siteSlicerState = await siteSlicerVisual.getSlicerState();

      if (siteSlicerVisual) {
        await siteSlicerVisual.setSlicerState(siteSlicerState);
      }
    } else {
      await siteSlicerVisual.setSlicerState({
        filters: [
          {
            $schema: constants.BASIC_FILTER,
            target: {
              table: 'vwSite',
              column: 'SiteName',
            },
            operator: 'In',
            values,
          },
        ],
      });
    }
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Formats a date string to include time component if not present.
 * @param {String} dateString - The date string to format.
 * @returns {String} - The formatted date string with time component.
 */
const formatDateWithTime = (dateString) => {
  if (!dateString) return dateString;
  // Check if the date already has time component (contains 'T')
  if (dateString.includes('T')) return dateString;
  // Add time component to date-only strings
  return `${dateString}T00:00:00.000`;
};

/**
 * Sets the date slicer visual based on the StartDate and EndDate parameters.
 * @param {Object} reportParameters - The report parameters.
 * @param {Array} visuals - The visuals array.
 * @returns {Promise<void>}
 */
const setDateSlicerVisual = async (reportParameters, visuals) => {
  try {
    const dateSlicerFilter = {
      $schema: constants.ADVANCED_FILTER,
      target: {
        table: 'vwReportingDateTime',
        column: 'Date',
      },
      logicalOperator: 'And',
      conditions: [
        {
          operator: 'GreaterThanOrEqual',
          value: formatDateWithTime(reportParameters.StartDate),
        },
        {
          operator: 'LessThanOrEqual',
          value: formatDateWithTime(reportParameters.EndDate),
        },
      ],
    };

    const dateSlicerVisual = visuals.filter((visual) => {
      return visual.type === 'slicer' && visual.title === 'Date Slicer';
    })[0];

    // uncomment to debug slicers
    // const thermostatSlicerState = await dateSlicerVisual.getSlicerState();
    // console.log('Thermostat Slicer State:', thermostatSlicerState);

    if (dateSlicerVisual) {
      await dateSlicerVisual.setSlicerState({
        filters: [dateSlicerFilter],
      });
    }
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Sets the Thermostat slicer visual based on the SiteName parameter.
 * @param {Object} reportParameters - The report parameters.
 * @param {Array} visuals - The visuals array.
 * @param {Boolean} unsetFirstLoad - Whether to unset the first load.
 * @returns {Promise<void>}
 */
const setThermostatSlicerVisual = async (
  reportParameters,
  visuals,
  unsetFirstLoad = false
) => {
  try {
    if (unsetFirstLoad) {
      firstLoad = false;
      firstSiteName = '';
    }
    const siteData = getSiteDataBySiteName(reportParameters);
    const { SiteName } = reportParameters;

    const thermostatSlicerVisual = visuals.find(
      (visual) =>
        visual.type === 'slicer' && visual.title === 'Thermostat Slicer'
    );

    if (!thermostatSlicerVisual) {
      return Promise.reject(new Error('Thermostat Slicer visual not found'));
    }
    const values = siteData ? [siteData.ThermostatName] : ['No Data to show'];

    if (!SiteName) {
      try {
        const thermostatSlicerState = await thermostatSlicerVisual.getSlicerState();
        await thermostatSlicerVisual.setSlicerState(thermostatSlicerState);
      } catch (error) {
        return Promise.reject(error);
      }
    } else {
      await thermostatSlicerVisual.setSlicerState({
        filters: [
          {
            $schema: constants.BASIC_FILTER,
            target: {
              table: 'vwControl',
              column: 'FriendlyName',
            },
            operator: 'In',
            values,
          },
        ],
      });
    }
  } catch (error) {
    console.log('Error in setThermostatSlicerVisual:', error);
    return Promise.reject(error);
  }
};

/**
 * Retrieves the first site data based on the SiteName parameter.
 * @param {Object} reportParameters - The report parameters.
 * @returns {Array} - The first site data.
 */
const getSiteDataBySiteName = (reportParameters) => {
  try {
    const { SiteName, Thermostats } = reportParameters;

    if (Thermostats) {
      const parsedData = JSON.parse(Thermostats);
      if (firstLoad) {
        firstSiteName = parsedData[0].SiteName.trim().toLowerCase();
      }
      const trimmedSiteName = firstSiteName.length
        ? firstSiteName
        : SiteName.trim().toLowerCase();

      const response = parsedData.find((data) => {
        return data.SiteName.trim().toLowerCase() === trimmedSiteName;
      });

      return !response && firstLoad ? parsedData[0] : response;
    }

    return null;
  } catch (error) {
    return Promise.reject(error);
  }
};

export default {
  setSlicers,
  setThermostatSlicerVisual,
};
